import React, { useState, useRef, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useTheme } from "@emotion/react";
import ScoreGraph from "../../../Charts/ScoreGraph";
import ScoreGraphCost from "../../../Charts/ScoreGraphCost";
import QuantGraphDetailed from "../../../Charts/QuantGraphDetailed";
import QuantGraphGlobal from "../../../Charts/QuantGraphGlobal";
import StepperButtons from "../../StepperButtons";
import DownloadIcon from "@mui/icons-material/Download";
import { ensureDesignScores } from "../DesignActions";
import { getDesignBackgroundColors } from "../../../../utils/chartPatterns";

export default function GraphSelector({ payload }) {
  const styling = useTheme();
  const dispatch = useDispatch();

  // Get Redux state
  const flatKpis = useSelector((state) => state.themes.flatKpis);
  const flatThemes = useSelector((state) => state.themes.flatThemes);
  
  // Get data from payload
  // const projectSegments = payload.phase.segments;
  const loopKpis = (payload?.loop?.loop_kpis || []).map(lk => ({...lk, kpi_details: flatKpis[lk.kpi]}));
  const basicloopLayers = payload?.loop?.loop_layers || [];
  const totalLayer = {
    id: 'total',
    layer: 'total',
    loop: payload.loop.id,
    layer_details: {
      description: "Totaal",
      name: "Totaal",
      project: null
    }
  }
  const loopLayers = [...basicloopLayers, totalLayer]
  const loopSegments = payload?.loop?.loop_segments || [];
  let loopThemes = payload?.loop?.loop_themes || [];
  loopThemes = loopThemes.map(lt => ({...lt, goals: loopKpis.filter(lk => lk.is_selected && lk.kpi_details.theme === lt.theme).flatMap(lk => lk.loop_goals || [])}))


  // Get designs and add reference design if exists
  let designs = [...payload?.loop?.designs || []].sort((a, b) => a.name.localeCompare(b.name));
  if (payload?.loop?.reference_design) {
    designs = [payload.loop.reference_design, ...designs];
  }

  // Filter selected KPIs and add cost KPI
  const selectedKpis = useMemo(() => {
    const loopSelectedKpis = loopKpis.filter((lk) => lk.is_selected).sort((a, b) => a.kpi_details.name.localeCompare(b.kpi_details.name));
    // Add cost KPI as a special case
    const costKpi = {
      id: "cost",
      kpi_details: { id: "cost", name: "Kosten", scale_type: -1 },
      is_selected: true,
    };
    return [...loopSelectedKpis, costKpi];
  }, [loopKpis]);

  const [selectedKpi, setSelectedKpi] = useState(selectedKpis.length > 0 ? selectedKpis[0].id : null);
  const [segmentFilter, setSegmentFilter] = useState(loopSegments.map((segment) => segment.id));
  const showGoalLine = loopSegments.length === segmentFilter.length;

  // Memoize project segment IDs
  // const projectSegmentIds = useMemo(() => {
  //   return projectSegments.map((segment) => segment.id);
  // }, [projectSegments]);

  // Refs for charts
  const chartRef = useRef(null);
  const chartRefs = useRef({});

  // Effect to ensure all designs have the required scores
  useEffect(() => {
    const ensureScoresForAllDesigns = async () => {
      for (const design of designs) {
        // Check if design has scores for all selected KPIs
        const selectedKpiIds = selectedKpis.map(kpi => kpi.kpi_details.id);
        const missingScores = selectedKpiIds.filter(kpiId => {
          if (kpiId === 'cost') return false; // Skip cost KPI as it's handled separately
          return !design.scores?.some(score => score.kpi === kpiId);
        });

        if (missingScores.length > 0) {
          console.log(`Ensuring scores for design ${design.name}, missing KPIs:`, missingScores);
          await ensureDesignScores(design.id, payload?.phase?.id, payload?.loop?.id, dispatch);
        }
      }
    };

    if (designs.length > 0 && selectedKpis.length > 0) {
      ensureScoresForAllDesigns();
    }
  }, [designs, selectedKpis, dispatch, payload?.phase?.id, payload?.loop?.id]);

  const handleKpiChange = (kpiId) => {
    setSelectedKpi(kpiId);
  };

  const handleFilterChange = (event) => {
    setSegmentFilter(event.target.value);
  };

  const handleDownload = () => {
    if (chartRef.current?.canvas) {
      const canvas = chartRef.current.canvas;
      const link = document.createElement("a");
      const currentKpi = selectedKpis.find((kpi) => kpi.id === selectedKpi);
      const phaseName = payload.phase?.phase_name || `Fase_${payload.phase?.id || 'unknown'}`;
      
      // Calculate loop number by finding the loop index within the phase
      let loopNumber = 'unknown';
      if (payload.phase?.loops && payload.loop?.id) {
        const loopIndex = payload.phase.loops.findIndex(loop => loop.id === payload.loop.id);
        if (loopIndex !== -1) {
          loopNumber = loopIndex + 1;
        }
      }
      
      link.download = `${currentKpi.kpi_details.name}_grafiek_${phaseName}_Ontwerpronde_${loopNumber}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    }
  };

  // Data constructor for qualitative (score) KPIs
  const qualitativeDataConstructor = (designs, kpiId) => {
    let ix = -1;
    return loopThemes.reduce((acc, loopTheme) => {
      loopTheme.goals
        .filter((goal) => flatKpis[goal.kpi].scale_type === 0 && goal.kpi === kpiId)
        .reduce((acc, goal) => {
          let name = flatKpis[goal.kpi].name;
          let value = goal.value;
          if (name.toUpperCase() === "SCORE") {
            name = `${flatThemes[loopTheme.theme].name}`;
          }

          ix += 1;
          const baseColors = [Object.values(styling.palette.graphics)[ix]];
          const allDesignColors = [baseColors[0], ...getDesignBackgroundColors(designs, [baseColors[0]])];
          
          acc.push({
            label: name,
            data: [
              value,
              ...designs.map((design) => {
                // Find the score for this KPI in the flat scores array
                const scoreObj = design.scores?.find((score) => score.kpi === goal.kpi);
                return scoreObj ? scoreObj.value : null;
              }),
            ],
            backgroundColor: allDesignColors,
          });
          return acc;
        }, acc);
      return acc;
    }, []);
  };

  // Data constructor for detailed quantitative KPIs (segmented)
  const detailedScoreDataConstructor = (designs, kpiId) => {
    let formattedData = [];
    try {
        formattedData = loopLayers.map((loopLayer, layerIndex) => {
        const baseColor = Object.values(styling.palette.graphics)[layerIndex];
        // const baseColor = 'orange'
        return {
          label: loopLayer.layer_details.name,
          data: designs.map((design) => {
            // Find the score for this KPI in the flat scores array
            const score = design.scores?.find((score) => score.kpi === kpiId);
            let layersToCheck = [loopLayer.id];

            if (score?.value) {
              return score.value
                .filter(
                  (segment) =>
                    segmentFilter.includes(segment.segment) ||
                    segment.segment === "total"
                )
                .reduce(
                  (acc, segment) =>
                    acc +
                    Number(
                      segment.values
                        .filter((layerScore) => layersToCheck.includes(layerScore.layer))
                        .reduce((acc, layerScore) => acc + Number(layerScore.value), 0)
                    ),
                  0
                );
            }
            return 0;
          }),
          type: "bar",
          backgroundColor: loopLayer.layer_details.name === "Totaal" ? "orange" : getDesignBackgroundColors(
            designs,
            [baseColor]
          ),
          // Store solid color for legend
          legendColor: baseColor,
        };
      });
    } catch (error) {
      console.error("Error constructing detailed score data:", error);
      console.log(`Showing global score for KPI ${kpiId}`);

      const fallbackColor = Object.values(styling.palette.graphics)[0];
      formattedData = [
        {
          label: "Totaal",
          data: designs.map((design) => {
            let value = "noscores";
            try {
              const score = design.scores?.find((score) => score.kpi === kpiId);
              value = score?.value;
            } catch (error) {
              console.error("Ontbrekend score object voor ontwerp:", design.name, error);
            }

            if (value === null) {
              console.log("Geen data voor ontwerp:", design.name);
              return 0;
            }

            if (typeof value === "object") {
              try {
                return Object.values(value).reduce((acc, val) => acc + val.value, 0);
              } catch (error) {
                console.error("data structuur verkeerd voor ontwerp:", design.name, error);
                return 0;
              }
            }

            return 0;
          }),
          type: "bar",
          backgroundColor: getDesignBackgroundColors(
            designs,
            [fallbackColor]
          ),
          // Store solid color for legend
          legendColor: fallbackColor,
        },
      ];
    }
    return formattedData;
  };

  // Data constructor for global quantitative KPIs
  const globalScoreDataConstructor = (designs, kpiId) => {
    const baseColor = Object.values(styling.palette.graphics)[0];
    return {
      label: "",
      data: designs.map((design) => {
        // Find the score for this KPI in the flat scores array
        const score = design.scores?.find((score) => score.kpi === kpiId);
        return score ? score.value : null;
      }),
      type: "bar",
      backgroundColor: getDesignBackgroundColors(
        designs,
        [baseColor]
      ),
      // Store solid color for legend
      legendColor: baseColor,
    };
  };

  if (designs.length === 0) {
    return (
      <Box>
        <Typography>
          Er zijn nog geen ontwerpen voor deze ontwerpronde. Voeg een ontwerp toe om de scores te kunnen bekijken.
        </Typography>
        <StepperButtons payload={payload} />
      </Box>
    );
  }

  if (selectedKpis.length === 0) {
    return (
      <Box>
        <Typography>
          Er zijn geen KPI's geselecteerd voor deze ontwerpronde. Selecteer KPI's in de setup om de scores te
          kunnen bekijken.
        </Typography>
        <StepperButtons payload={payload} />
      </Box>
    );
  }

  const currentKpi = selectedKpis.find((kpi) => kpi.id === selectedKpi);

  // Find relevant loop themes and goals for the selected KPI
  const relevantLoopThemes = loopThemes.filter((loopTheme) =>
    loopTheme.goals.some((goal) => goal.kpi === currentKpi?.kpi_details?.id)
  );

  return (
    <Box>
      {/* KPI Selection buttons */}
      <Box sx={{ mb: 3, mt: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Typography variant="h6">Selecteer KPI:</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {selectedKpis.map((kpi) => (
            <Tooltip key={kpi.id} title={kpi.kpi_details.description || ""} enterDelay={1000}>
              <Button
                onClick={() => handleKpiChange(kpi.id)}
                variant={selectedKpi === kpi.id ? "contained" : "outlined"}
                size="small"
                sx={{
                  minWidth: 120,
                  textTransform: "none",
                  fontWeight: selectedKpi === kpi.id ? "bold" : "normal",
                  borderRadius: 3,
                }}
              >
                <Typography variant="body2">{kpi.kpi_details.name}{kpi.kpi_details.unit && ` (${kpi.kpi_details.unit})`}</Typography>
              </Button>
            </Tooltip>
          ))}
        </Box>
      </Box>

      {/* KPI name and download button */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="h6">{currentKpi.kpi_details.name}</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          sx={{ textTransform: "none" }}
        >
          Download
        </Button>
      </Box>

      {/* Display the selected KPI graph */}
      <Box>
        {currentKpi?.kpi_details?.id === "cost" && (
          // Cost graph
          <Grid item xs={12} sx={{ maxHeight: 350, minHeight: 250 }}>
            <ScoreGraphCost ref={chartRef} designs={designs} variants={designs.map((design) => design.name)} />
          </Grid>
        )}

        {currentKpi?.kpi_details?.scale_type === 0 && (
          // Qualitative (score) KPI
          <Grid container>
            <Grid item xs={12} sx={{ maxHeight: 350, minHeight: 250 }}>
              <ScoreGraph
                 ref={chartRef}
                 initData={qualitativeDataConstructor(designs, currentKpi.kpi_details.id)}
                 variants={designs.map((design) => design.name)}
                 designs={designs}
                 kpi={currentKpi.kpi_details}
               />
            </Grid>
          </Grid>
        )}

        {currentKpi?.kpi_details?.scale_type > 0 && (
          // Quantitative KPIs
          <Grid container spacing={1}>
            {/* Segment filter for quantitative KPIs */}
            {currentKpi?.kpi_details?.scale_type ===  1 && <Grid item xs={12}>
              {/* <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}> */}
                <Box>
                  <FormControl>
                    <InputLabel id="segment-filter-label">Dijkvakken</InputLabel>
                    <Select
                      autoWidth
                      multiple
                      multiline
                      label={"Dijkvakken"}
                      value={segmentFilter}
                      sx={{ width: "fit-content" }}
                      onChange={handleFilterChange}
                      renderValue={(selected) => (
                        <Box
                          sx={{
                            display: "inline-flex",
                            flexWrap: segmentFilter.length < 10 ? "no-wrap" : "wrap",
                            gap: 0.2,
                          }}
                        >
                          {selected.map((value) => (
                            <Chip
                              size="small"
                              key={value}
                              label={loopSegments.find((segment) => segment.id === value)?.name || value}
                              color="primary"
                            />
                          ))}
                        </Box>
                      )}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                      }}
                    >
                      {loopSegments.map((segment) => (
                        <MenuItem key={segment.id} value={segment.id}>
                          {segment.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              {/* </Stack> */}
            </Grid>}

            {/* Display graphs for each relevant goal */}
            {relevantLoopThemes.map((loopTheme) =>
              loopTheme.goals
                .filter((goal) => goal.kpi === currentKpi.kpi_details.id && flatKpis[goal.kpi]?.scale_type > 0)
                .map((loopGoal, goalIndex) => {
                  const kpi = flatKpis[loopGoal.kpi];
                  const chartKey = `${kpi.name}-${kpi.scale_type === 1 ? "detailed" : "global"}`;

                  return (
                    <Grid
                      sx={{ maxHeight: 400, minHeight: 200, minWidth: 400 }}
                      item
                      xs={12}
                      xl={6}
                      key={`goal_${loopGoal.id}`}
                    >
                      {kpi.scale_type === 1 ? (
                        <QuantGraphDetailed
                          key={`goal_${loopGoal.id || goalIndex}`}
                          variants={designs.map((design) => design.name)}
                          ref={chartRef}
                          layers={loopLayers}
                          segments={loopSegments}
                          initData={detailedScoreDataConstructor(designs, currentKpi.kpi_details.id)}
                          segmentFilter={segmentFilter}
                          kpi={kpi}
                          goal={loopGoal}
                          showGoalLine={showGoalLine}
                        />
                      ) : (
                        <QuantGraphGlobal
                          ref={chartRef}
                          variants={designs.map((design) => design.name)}
                          initData={globalScoreDataConstructor(designs, currentKpi.kpi_details.id)}
                          kpi={kpi}
                          goal={loopGoal}
                        />
                      )}
                    </Grid>
                  );
                })
            )}
          </Grid>
        )}
      </Box>
    </Box>
  );
}

GraphSelector.propTypes = {
  payload: PropTypes.object.isRequired,
};
